import { Mail, MapPin, Monitor, Phone } from "@tamagui/lucide-icons";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Anchor, Heading, Image, Text, XStack, YStack } from "tamagui";

import { homeServiceLogo } from "../../assets/images";
import { DividerLine } from "../../components/DividerLine";
import { IconContainer } from "../../components/IconContainer";

// ponytail: contact details are hardcoded placeholders carried over from the
// old Service Contact + Help pages; swap for real values (or a ServiceCompany
// record) when ops supplies them.
const company = {
  name: "Home Service Company",
  address: "275 Old County Line Rd P Westerville, OH 43081",
  phone: "(123) 456-7890",
  email: "contact@homeserviceco.com",
};

const lines = [
  { icon: Mail, labelKey: "help.generalEmailSupport", value: company.email },
  { icon: Phone, labelKey: "help.setupSupport", value: "(913) 123-6322" },
  { icon: Monitor, labelKey: "help.appSupport", value: "(331) 631-6562" },
];

const ContactLink = ({ value }: { value: string }) => (
  <Anchor
    href={value.includes("@") ? `mailto:${value}` : `tel:${value}`}
    color="$hgPrimary"
    fontSize="$4"
    fontWeight="700"
    // 44px tap target on phones; the text stays centred in it.
    $sm={{ minHeight: 44, display: "flex", alignItems: "center" }}
  >
    {value}
  </Anchor>
);

const Row = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <XStack alignItems="center" gap="$2">
    <IconContainer>{icon}</IconContainer>
    {children}
  </XStack>
);

export const Support = () => {
  const { t } = useTranslation();

  return (
    <YStack gap="$4" width="100%">
      <Heading size="$14" fontSize="$14" $xs={{ fontSize: "$12" }}>
        {t("sidebar.support")}
      </Heading>

      <YStack
        gap="$3.5"
        backgroundColor="$transparentBorder"
        padding="$4"
        borderRadius="$4"
      >
        <Image
          source={{ uri: homeServiceLogo }}
          width={147}
          height={56}
          objectFit="contain"
        />
        <DividerLine />
        <Heading size="$14" color="$white12" fontSize="$6" lineHeight="$6">
          {company.name}
        </Heading>
        <Row icon={<MapPin size={20} />}>
          <Text fontSize="$4" color="$white12">
            {company.address}
          </Text>
        </Row>
        <Row icon={<Phone size={20} />}>
          <ContactLink value={company.phone} />
        </Row>
        <Row icon={<Mail size={20} />}>
          <ContactLink value={company.email} />
        </Row>
      </YStack>

      <XStack width="100%" gap="$3.5" flexWrap="wrap">
        {lines.map(({ icon: Icon, labelKey, value }) => (
          <YStack
            key={labelKey}
            flex={1}
            minWidth={220}
            gap="$3.5"
            backgroundColor="$transparentBorder"
            padding="$3.5"
            borderRadius="$4"
            justifyContent="space-between"
          >
            <IconContainer>
              <Icon size={20} />
            </IconContainer>
            <Text fontSize="$4" color="$white12" paddingBottom="$4">
              {t(labelKey)}
            </Text>
            <ContactLink value={value} />
          </YStack>
        ))}
      </XStack>
    </YStack>
  );
};

export default Support;
